import { NextRequest, NextResponse } from 'next/server';
import { AxiosInstance } from 'axios';
import { environment } from '@helpers/environment';

/**
 * Setup Axios interceptor for location mocking
 * This intercepts currency_data responses and overrides location fields
 * when mocking cookies are present. Only enabled in non-production environments
 * @param axiosInstance - The Axios instance to add the interceptor to
 */
export function setupLocationMockingInterceptor(axiosInstance: AxiosInstance): void {
  // Only setup interceptor in non-production environments
  if (environment.isProduction) {
    return;
  }

  axiosInstance.interceptors.response.use(
    (response) => {
      // Check if this is a currency_data response
      if (response.config.url?.includes('currency_data')) {
        // Check for location mocking cookies (only available in browser)
        if (typeof window !== 'undefined' && document.cookie) {
          const cookies = document.cookie.split(';').reduce((acc, cookie) => {
            const [key, value] = cookie.trim().split('=');
            acc[key] = value;
            return acc;
          }, {} as Record<string, string>);

          const mockCity = cookies.mock_city;
          const mockState = cookies.mock_state;
          const mockCountry = cookies.mock_country;

          // If location mocking is active, override the location fields
          if (mockCity && mockState && mockCountry) {
            const decodedCountry = decodeURIComponent(mockCountry);
            response.data = {
              ...response.data,
              city: decodeURIComponent(mockCity),
              state: decodeURIComponent(mockState),
              country: decodedCountry,
              user_from_usa: decodedCountry === 'US',
            };
          }
        }
      }
      return response;
    },
    (error) => Promise.reject(error),
  );
}

/**
 * Apply location mocking to a request using URL parameters
 * Only activates when all required parameters are provided
 * Only enabled in non-production environments
 * @param request - The incoming NextRequest
 * @returns Modified NextResponse with cookies containing location overrides,
 * or null if no mocking needed
 */
export function applyLocationMockingMiddleware(request: NextRequest): NextResponse | null {
  // Only apply location mocking in non-production environments
  if (environment.isProduction) {
    return null;
  }

  const { searchParams } = request.nextUrl;

  // Check if all required mocking parameters are present
  const mockCity = searchParams.get('mock_city');
  const mockStateCode = searchParams.get('mock_state_code');
  const mockStateName = searchParams.get('mock_state_name');
  const mockCountry = searchParams.get('mock_country');

  if (!mockCity || !mockStateCode || !mockStateName || !mockCountry) {
    return null;
  }

  // Create response with location override cookies
  const response = NextResponse.next();

  // Set server-side cookies (for Cloudflare header simulation)
  response.cookies.set('cf-ipcity', mockCity, { httpOnly: true });
  response.cookies.set('cf-region', mockStateCode, { httpOnly: true });
  response.cookies.set('cf-ipcountry', mockCountry, { httpOnly: true });

  // Set client-side cookies (for axios interceptor)
  response.cookies.set('mock_city', mockCity, { httpOnly: false });
  response.cookies.set('mock_state', mockStateName, { httpOnly: false });
  response.cookies.set('mock_country', mockCountry, { httpOnly: false });

  return response;
}

/**
 * Helper function to construct a URL with location mocking parameters
 * @param baseUrl - The base URL
 * @param city - City name
 * @param stateCode - State code (e.g., "NV") for server-side mocking
 * @param stateName - Full state name (e.g., "Nevada") for client-side mocking
 * @param country - Country code
 * @returns URL with mock location parameters
 */
export function createMockLocationUrl(
  baseUrl: string,
  city: string,
  stateCode: string,
  stateName: string,
  country: string,
): string {
  const url = new URL(baseUrl);
  url.searchParams.set('mock_city', city);
  url.searchParams.set('mock_state_code', stateCode);
  url.searchParams.set('mock_state_name', stateName);
  url.searchParams.set('mock_country', country);
  return url.toString();
}
